import React, { useState, useEffect } from 'react';
import { useWalkthrough } from './useWalkthrough';
import WalkthroughOverlay from './WalkthroughOverlay';
import WalkthroughTooltip from './WalkthroughTooltip';
import { getElementPosition, getOptimalTooltipPosition, scrollToElement, isElementInViewport } from '../../lib/walkthroughUtils';

/**
 * WalkthroughProvider Component
 * Main orchestrator for the walkthrough feature
 * Wraps mockup content and manages the walkthrough lifecycle
 */
const WalkthroughProvider = ({ config, mockupId, children }) => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    closeWalkthrough
  } = useWalkthrough(config);

  const [elementRect, setElementRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  // Update element position and tooltip position when step changes
  useEffect(() => {
    if (!isActive || !currentStep) {
      setElementRect(null);
      setTooltipPosition(null);
      return;
    }

    // Wait a bit for DOM to be ready
    const updatePositions = () => {
      const padding = currentStep.highlightPadding || 8;
      const rect = getElementPosition(currentStep.target, padding);

      if (rect) {
        setElementRect(rect);

        // Scroll to element if not in viewport
        if (!isElementInViewport(rect.element)) {
          scrollToElement(rect.element);
        }

        // Calculate tooltip position
        const tooltipPos = getOptimalTooltipPosition(rect, currentStep.placement);
        setTooltipPosition(tooltipPos);
      } else {
        console.warn(`Walkthrough: Could not find element for step ${currentStepIndex + 1}`);
        setElementRect(null);
        setTooltipPosition(null);
      }
    };

    // Delay to allow for any animations or transitions
    const timeoutId = setTimeout(updatePositions, 100);

    return () => clearTimeout(timeoutId);
  }, [isActive, currentStep, currentStepIndex]);

  // Update positions on window resize or scroll
  useEffect(() => {
    if (!isActive || !currentStep) return;

    const handleResize = () => {
      const padding = currentStep.highlightPadding || 8;
      const rect = getElementPosition(currentStep.target, padding);

      if (rect) {
        setElementRect(rect);
        const tooltipPos = getOptimalTooltipPosition(rect, currentStep.placement);
        setTooltipPosition(tooltipPos);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isActive, currentStep]);

  // Don't render anything if walkthrough is not active
  if (!isActive) {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Overlay with spotlight effect */}
      <WalkthroughOverlay
        elementRect={elementRect}
        padding={currentStep?.highlightPadding || 8}
      />

      {/* Tooltip with step information and controls */}
      <WalkthroughTooltip
        step={currentStep}
        position={tooltipPosition}
        currentStepIndex={currentStepIndex}
        totalSteps={totalSteps}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onPrevious={previousStep}
        onNext={nextStep}
        onClose={closeWalkthrough}
      />
    </>
  );
};

export default WalkthroughProvider;
